import { useRef, useCallback } from 'react';

import {
  usePaypalCreateMutation,
  PaypalCreateRequest,
  PaypalCreateResponse,
} from '@/api/payments/paypalCreate';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { useClient } from '@/services/client/useClient';
import { useCartContext } from '@/services/cart/CartContext';
import { useSite, SiteData, SiteUrls } from '@/services/site/useSite';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { setTransactionId } from '@/features/paymentProcess/transactionSlice';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';

const buildCreatePayload = (
  paymentSelectState: PaymentSelectState,
  accountIdentifier: string,
  client: string,
  siteData: SiteData,
  siteUrls: SiteUrls,
  campaignCode: string | null,
  isPayin: boolean,
  cartId?: string,
): PaypalCreateRequest => ({
  client,
  account_identifier: accountIdentifier,
  order_reference: paymentSelectState.orderReference,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  giftaid: Number(paymentSelectState.giftaid),
  campaign: campaignCode ?? siteData.campaign.name,
  transSource: siteData.campaign.transSource,
  transSourceUrl: siteUrls.transSourceUrl,
  transType: isPayin ? 'payin' : siteData.campaign.transType,
  cartId: cartId ?? siteData.defaultCart,
});

export function usePaypalCreate() {
  const onSuccess = useRef<((data: PaypalCreateResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const { cartId, getCartFinancialAttribution } = useCartContext();

  // Ref ensures we always read the latest state at call time. The PayPal SDK may capture
  // createOrder at mount of the payment state, so it can lead to a stale closure
  // of your giftaid selection, and send 0 when we're expecting 1 after having checked the box
  const paymentSelectStateRef = useRef(paymentSelectState);
  paymentSelectStateRef.current = paymentSelectState;

  const [paypalCreateTrigger] = usePaypalCreateMutation();

  const { getProviderPaypalAccountIdentifier } = usePaymentsConfig();
  const paypalAccountIdentifier = getProviderPaypalAccountIdentifier();
  const { getClientId } = useClient();
  const { getSiteData, getSiteUrls } = useSite();
  const { selectedJourney } = useJourneyOptions();
  const isPayin = selectedJourney === 'payin';

  // Trigger payment-service create
  // API Reference: https://developer.paypal.com/sdk/js/reference/#link-createorder
  const triggerCreateApi = useCallback(async (
    callbacks?: {
      onSuccess?: (data: PaypalCreateResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    // Require paypal provider config
    if (!paypalAccountIdentifier) {
      logger.error('Error accessing Paypal config');
      if (onError.current) {
        onError.current(new Error('Error accessing Paypal config'));
      }
      throw new Error('Error accessing Paypal config');
    }

    const currentPaymentSelectState = paymentSelectStateRef.current;

    // Paypal provider does not support fixedTransactionId used by direct (prize) journey
    if (currentPaymentSelectState.fixedTransactionId) {
      logger.error('Paypal not supported when fixedTransactionId is set');
      if (onError.current) {
        onError.current(new Error('Paypal not supported when fixedTransactionId is set'));
      }
      throw new Error('Paypal not supported when fixedTransactionId is set');
    }

    const payload = buildCreatePayload(
      currentPaymentSelectState,
      paypalAccountIdentifier,
      getClientId(),
      getSiteData(),
      getSiteUrls(),
      getCartFinancialAttribution()?.campaignCode ?? null,
      isPayin,
      cartId,
    );
    const result = await paypalCreateTrigger(payload);

    // Handle payment-service create errors
    if (!!result?.error || result?.data?.message !== 'success' || !result?.data?.data?.provider_reference) {
      logger.error('Error creating Paypal transaction');
      logger.error(result.error);

      if (onError.current) {
        onError.current(result.error);
      }
      throw new Error('Error creating Paypal transaction');
    }

    // Dispatch payment-service create data to transactionState
    dispatch(setTransactionId(result.data.data.transactionId));

    if (onSuccess.current) {
      onSuccess.current(result.data);
    }

    // Return provider_reference as expected return value for <PayPalButtons> createOrder()
    return result.data.data.provider_reference;
  }, [
    paypalAccountIdentifier,
    getClientId,
    getSiteData,
    getSiteUrls,
    getCartFinancialAttribution,
    paypalCreateTrigger,
    dispatch,
    isPayin,
    cartId,
  ]);

  return {
    triggerCreateApi,
  };
}
